import React, { useMemo } from 'react'

import { Typography, TypographyProps } from '@mui/material'

/**
 * Tidies analyst-authored plain text so its line structure survives rendering:
 * CRLF/CR become LF, whitespace-only lines become empty, three or more newlines
 * collapse to a single blank line, and the ends are trimmed. A single newline
 * stays a line break and a double newline stays a paragraph gap.
 */
export function normalizeParagraphText(text: string | null | undefined): string {
  if (!text) return ''
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/[^\S\n]+\n/g, '\n')
    .replace(/\n[^\S\n]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

interface PlainTextParagraphsProps extends Omit<TypographyProps, 'children'> {
  text: string | null | undefined
}

/**
 * Renders plain text (never HTML) with its original paragraph and line breaks.
 *
 * `white-space: pre-line` is what the GRTooltip surface applies to everything
 * inside it, so a definition that looks right in a tooltip lost its breaks as
 * soon as it was rendered on a page. Using this component on every surface that
 * shows the same copy keeps them identical. The text is a React child, so it is
 * escaped like any other string.
 */
export const PlainTextParagraphs: React.FC<PlainTextParagraphsProps> = ({ text, sx, variant = 'body2', ...rest }) => {
  const normalized = useMemo(() => normalizeParagraphText(text), [text])

  if (!normalized) return null

  return (
    <Typography variant={variant} sx={[{ whiteSpace: 'pre-line' }, ...(Array.isArray(sx) ? sx : [sx])]} {...rest}>
      {normalized}
    </Typography>
  )
}
